// ============================================================================
// CHARGE TYPE TYPES
// ============================================================================
// The charge-type master (pms.tbl_charge_type) — what a folio can be charged
// for. Tenant-wide; tax per property is set on Charge Tax Mappings.
// Matches crs.Core.DTOs.ChargeTypeModule.ChargeTypeDtos.

export type ChargeRevenueGroup = 'ROOM' | 'FOOD_BEVERAGE' | 'OTHER';

export const REVENUE_GROUP_OPTIONS: Array<{ value: ChargeRevenueGroup; label: string }> = [
  { value: 'ROOM', label: 'Room' },
  { value: 'FOOD_BEVERAGE', label: 'Food & Beverage' },
  { value: 'OTHER', label: 'Other services' },
];

export const revenueGroupLabel = (v?: string | null): string =>
  REVENUE_GROUP_OPTIONS.find((o) => o.value === v)?.label ?? (v || '');

/**
 * Place-of-supply nature (PMS slice 5, P2-16) — the tax engine's natures. Decides
 * CGST+SGST against IGST for a charge posted at the desk. Blank = Accommodation.
 */
export const SUPPLY_NATURE_OPTIONS: Array<{ value: string; label: string; hint: string }> = [
  { value: 'ACCOMMODATION',       label: 'Accommodation',            hint: 'Where the hotel is (room, meals, add-ons)' },
  { value: 'PERFORMANCE',         label: 'Restaurant / personal service', hint: 'Where it is performed' },
  { value: 'SERVICE_GENERAL',     label: 'General service',          hint: "Registered payer's state, else the hotel's (laundry, misc.)" },
  { value: 'GOODS',               label: 'Goods',                    hint: 'Where delivered (minibar, shop)' },
  { value: 'PASSENGER_TRANSPORT', label: 'Passenger transport',      hint: 'Where the journey starts' },
  { value: 'IMMOVABLE_PROPERTY',  label: 'Service on property',      hint: 'Where the property is' },
  { value: 'EVENT_ADMISSION',     label: 'Event admission',          hint: 'Where the event is held' },
  { value: 'EVENT_ORGANISATION',  label: 'Event organisation',       hint: 'Registered payer, else the venue' },
  { value: 'ADDRESS_ON_RECORD',   label: 'Address on record',        hint: "The payer's address on record" },
];

export const supplyNatureLabel = (v?: string | null): string =>
  SUPPLY_NATURE_OPTIONS.find((o) => o.value === v)?.label ?? (v ? v : 'Accommodation');

/**
 * What a charge is (slice 7c) — decides the voucher, the tax and the invoice:
 * a service and a recovery are taxed through the charge type's mapping; a
 * disbursement is paid out for the guest and recovered at cost, never taxed,
 * and billed as a reimbursement outside the invoice's taxable value.
 */
export type ChargeNature = 'SERVICE' | 'RECOVERY' | 'DISBURSEMENT';

export const CHARGE_NATURE_OPTIONS: Array<{ value: ChargeNature; label: string; hint: string }> = [
  { value: 'SERVICE',      label: 'Service',                 hint: 'Hotel revenue (laundry, transfers, extra bed) — taxed as mapped.' },
  { value: 'RECOVERY',     label: 'Recovery (damage / loss)', hint: 'Recovered from the guest — taxed as mapped; a reason is required.' },
  { value: 'DISBURSEMENT', label: 'Disbursement (at cost)',  hint: 'Paid out for the guest — no tax; the supplier bill is required; a reimbursement on the invoice.' },
];

export const chargeNatureLabel = (v?: string | null): string =>
  CHARGE_NATURE_OPTIONS.find((o) => o.value === v)?.label ?? 'Service';

export interface ChargeType {
  id: string;
  chargeCode: string;
  chargeName: string;
  description?: string | null;
  revenueGroup: ChargeRevenueGroup;
  /** false = internal (house use, complimentary cost): recorded, never invoiced. */
  isGuestBillable: boolean;
  /** Whether Front Office may post it by hand. false for ROOM (night audit only). */
  allowManualPost: boolean;
  /** Shipped with CRS: only description and display order can change. */
  isSystem: boolean;
  /** Place-of-supply nature; null = Accommodation. */
  supplyNature?: string | null;
  /** SERVICE / RECOVERY / DISBURSEMENT (slice 7c). */
  chargeNature?: ChargeNature | null;
  displayOrder: number;
  isActive: boolean;
  statusCode: string;
  createdAt?: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
  rowVersion: number;
}

export interface CreateChargeTypeRequest {
  chargeCode: string;
  chargeName: string;
  description?: string | null;
  revenueGroup: ChargeRevenueGroup;
  isGuestBillable: boolean;
  allowManualPost: boolean;
  supplyNature?: string | null;
  chargeNature?: ChargeNature | null;
  displayOrder: number;
  isActive: boolean;
}

/** Everything but the code — it is immutable (referenced by folio charges, outlets and tax mappings). */
export interface UpdateChargeTypeRequest {
  chargeName: string;
  description?: string | null;
  revenueGroup: ChargeRevenueGroup;
  isGuestBillable: boolean;
  allowManualPost: boolean;
  supplyNature?: string | null;
  chargeNature?: ChargeNature | null;
  displayOrder: number;
  isActive: boolean;
  rowVersion: number;
}

export interface ChargeTypeSearchRequest {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  revenueGroup?: ChargeRevenueGroup;
  includeInactive?: boolean;
}
